import { PlusOutlined } from '@ant-design/icons';
import { ModalForm, PageContainer, ProFormSelect, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Popconfirm } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { CronFormItem, formatCronDescription } from '../components/CronEditor';
import HarnessOrganization from '../components/HarnessOrganization';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import Page403 from '@/common/views/Page403';
import {
  createAgentSchedule,
  queryAgents,
  queryAgentRuns,
  queryAgentSchedules,
  queryAvailableAgents,
  removeAgentSchedule,
  triggerAgentRun,
  updateAgentSchedule,
} from '../services/agents';

const AGENT_LIFECYCLE_STATUS = {
  active: {
    text: '启用',
    status: 'Success',
  },
  archived: {
    text: '归档',
    status: 'Default',
  },
  paused: {
    text: '暂停',
    status: 'Warning',
  },
};

const RUN_STATUS = {
  CANCELLED: {
    text: '已取消',
    status: 'Default',
  },
  DELAYED: {
    text: '已延迟',
    status: 'Warning',
  },
  ENQUEUED: {
    text: '排队中',
    status: 'Processing',
  },
  ERROR: {
    text: '失败',
    status: 'Error',
  },
  MAX_RECOVERY_ATTEMPTS_EXCEEDED: {
    text: '恢复失败',
    status: 'Error',
  },
  PENDING: {
    text: '运行中',
    status: 'Processing',
  },
  SUCCESS: {
    text: '成功',
    status: 'Success',
  },
};

const statusOptions = [
  {
    label: '启用',
    value: 'active',
  },
  {
    label: '暂停',
    value: 'paused',
  },
  {
    label: '归档',
    value: 'archived',
  },
];

const agentTabList = [
  {
    key: 'agents',
    tab: '智能体',
  },
  {
    key: 'schedules',
    tab: '自动运行',
  },
  {
    key: 'runs',
    tab: '运行记录',
  },
  {
    key: 'harness',
    tab: '创作助手',
  },
];

const getAgentTabKey = (query) => {
  const tab = Array.isArray(query.tab) ? query.tab.at(-1) : query.tab;
  return agentTabList.some((item) => item.key === tab) ? tab : agentTabList[0].key;
};

const formatRunSummary = (summary) => {
  if (!summary || typeof summary !== 'object') {
    return '-';
  }
  const parts = [];
  if (summary.successCount !== undefined) {
    parts.push(`成功 ${summary.successCount}`);
  }
  if (summary.failureCount !== undefined) {
    parts.push(`失败 ${summary.failureCount}`);
  }
  return parts.length > 0 ? parts.join(' / ') : '-';
};

const AgentScheduleFields = ({ organizationId }) => (
  <>
    <ProFormSelect
      name="agentId"
      label="智能体"
      request={async () => {
        const agents = await queryAvailableAgents({ organizationId });
        return agents.map((agent) => ({
          label: agent.title,
          value: agent.agentId,
        }));
      }}
      rules={[
        {
          required: true,
          message: '请选择智能体',
        },
      ]}
    />
    <CronFormItem />
    <ProFormSelect
      name="status"
      label="状态"
      options={statusOptions}
      initialValue="active"
      rules={[
        {
          required: true,
          message: '请选择状态',
        },
      ]}
    />
  </>
);

AgentScheduleFields.propTypes = {
  organizationId: PropTypes.string.isRequired,
};

const Agents = () => {
  const agentActionRef = useRef(null);
  const scheduleActionRef = useRef(null);
  const runActionRef = useRef(null);
  const { organizationId } = useOrganizationPath();
  const { session } = useAppContext();
  const [query, setQuery] = useQueryParams();
  const activeTabKey = getAgentTabKey(query);
  const { message } = App.useApp();

  const reloadAll = () => {
    agentActionRef.current?.reload?.();
    scheduleActionRef.current?.reload?.();
    runActionRef.current?.reload?.();
  };

  const { mutateAsync: createSchedule, isPending: creatingSchedule } = useMutation({
    mutationFn: createAgentSchedule,
    onSuccess: () => {
      message.success('自动运行已创建');
      reloadAll();
    },
    onError: () => {
      message.error('自动运行创建失败，请重试');
    },
  });
  const { mutateAsync: updateSchedule, isPending: updatingSchedule } = useMutation({
    mutationFn: updateAgentSchedule,
    onSuccess: () => {
      message.success('自动运行已更新');
      reloadAll();
    },
    onError: () => {
      message.error('自动运行更新失败，请重试');
    },
  });
  const { mutateAsync: removeSchedule, isPending: removingSchedule } = useMutation({
    mutationFn: removeAgentSchedule,
    onSuccess: () => {
      message.success('自动运行已删除');
      reloadAll();
    },
    onError: () => {
      message.error('自动运行删除失败，请重试');
    },
  });
  const { mutateAsync: triggerRun, isPending: triggeringRun } = useMutation({
    mutationFn: triggerAgentRun,
    onSuccess: () => {
      message.success('Agent Run 已提交');
      runActionRef.current?.reload?.();
    },
    onError: () => {
      message.error('Agent Run 提交失败，请重试');
    },
  });

  const agentColumns = useMemo(
    () => [
      {
        title: '智能体',
        dataIndex: 'title',
      },
      {
        title: 'Agent ID',
        dataIndex: 'agentId',
        hideInTable: true,
        search: false,
      },
      {
        title: '说明',
        dataIndex: 'description',
        search: false,
      },
      {
        title: '操作',
        valueType: 'option',
        render: (_, record) => [
          <Button
            key="trigger"
            type="link"
            loading={triggeringRun}
            onClick={() =>
              triggerRun({
                data: {
                  agentId: record.agentId,
                  organizationId,
                },
              })
            }
          >
            手动运行
          </Button>,
        ],
      },
    ],
    [organizationId, triggerRun, triggeringRun],
  );

  const scheduleColumns = useMemo(
    () => [
      {
        title: '智能体',
        dataIndex: 'agentTitle',
        valueType: 'select',
        renderText: (_, record) => record.agentTitle || record.agentId,
        fieldProps: {
          fieldNames: {
            label: 'title',
            value: 'agentId',
          },
        },
        request: () => queryAvailableAgents({ organizationId }),
        search: {
          transform: (value) => ({
            agentId: value,
          }),
        },
      },
      {
        title: '定时规则',
        dataIndex: 'cron',
        renderText: (cron) => formatCronDescription(cron),
        search: false,
      },
      {
        title: '状态',
        dataIndex: 'status',
        valueType: 'select',
        valueEnum: AGENT_LIFECYCLE_STATUS,
        fieldProps: {
          options: statusOptions,
        },
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        valueType: 'dateTime',
        search: false,
      },
      {
        title: '操作',
        valueType: 'option',
        render: (_, record) => [
          <ModalForm
            key="edit"
            title="修改自动运行"
            trigger={<Button type="link">修改</Button>}
            width={480}
            initialValues={record}
            modalProps={{
              destroyOnHidden: true,
              okButtonProps: {
                loading: updatingSchedule,
              },
            }}
            onFinish={async (values) => {
              try {
                await updateSchedule({
                  data: {
                    id: record.id,
                    organizationId,
                    ...values,
                  },
                });
                return true;
              } catch {
                return false;
              }
            }}
          >
            <CronFormItem />
            <ProFormSelect
              name="status"
              label="状态"
              options={statusOptions}
              rules={[{ required: true, message: '请选择状态' }]}
            />
          </ModalForm>,
          <Popconfirm
            key="remove"
            title="删除自动运行"
            description="确认删除这个自动运行？"
            okText="确认"
            cancelText="取消"
            onConfirm={() =>
              removeSchedule({
                data: {
                  id: record.id,
                  organizationId,
                },
              })
            }
          >
            <Button type="link" danger loading={removingSchedule}>
              删除
            </Button>
          </Popconfirm>,
        ],
      },
    ],
    [organizationId, removeSchedule, removingSchedule, updateSchedule, updatingSchedule],
  );

  const runColumns = [
    {
      title: '智能体',
      dataIndex: 'agentId',
      valueType: 'select',
      render: (_, record) => record.agentTitle || record.agentId,
      fieldProps: {
        fieldNames: {
          label: 'title',
          value: 'agentId',
        },
      },
      request: () => queryAvailableAgents({ organizationId }),
    },
    {
      title: '状态',
      dataIndex: 'status',
      valueEnum: RUN_STATUS,
    },
    {
      title: '汇总',
      dataIndex: 'summary',
      renderText: formatRunSummary,
      search: false,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      valueType: 'dateTime',
      search: false,
    },
  ];

  if (!['owner', 'admin'].includes(session.role)) return <Page403 />;

  return (
    <PageContainer
      tabList={agentTabList}
      tabActiveKey={activeTabKey}
      onTabChange={(key) => {
        setQuery({ tab: key });
      }}
    >
      {activeTabKey === 'harness' ? (
        <HarnessOrganization key={`${organizationId}:${session.user.id}`} />
      ) : null}
      {activeTabKey === 'agents' ? (
        <ProTable
          actionRef={agentActionRef}
          rowKey="agentId"
          headerTitle="智能体"
          request={(params) => queryAgents({ ...params, organizationId })}
          columns={agentColumns}
          search={{
            labelWidth: 96,
          }}
        />
      ) : null}
      {activeTabKey === 'schedules' ? (
        <ProTable
          actionRef={scheduleActionRef}
          rowKey="id"
          headerTitle="自动运行"
          request={(params) => queryAgentSchedules({ ...params, organizationId })}
          columns={scheduleColumns}
          search={{
            labelWidth: 96,
          }}
          toolBarRender={() => [
            <ModalForm
              key="create"
              title="创建自动运行"
              trigger={
                <Button type="primary" icon={<PlusOutlined />}>
                  创建自动运行
                </Button>
              }
              width={480}
              modalProps={{
                destroyOnHidden: true,
                okButtonProps: {
                  loading: creatingSchedule,
                },
              }}
              onFinish={async (values) => {
                try {
                  await createSchedule({
                    data: {
                      organizationId,
                      ...values,
                    },
                  });
                  return true;
                } catch {
                  return false;
                }
              }}
            >
              <AgentScheduleFields organizationId={organizationId} />
            </ModalForm>,
          ]}
        />
      ) : null}
      {activeTabKey === 'runs' ? (
        <ProTable
          actionRef={runActionRef}
          rowKey="workflowId"
          headerTitle="运行记录"
          request={(params) => queryAgentRuns({ ...params, organizationId })}
          columns={runColumns}
          search={{
            labelWidth: 96,
          }}
        />
      ) : null}
    </PageContainer>
  );
};

export default Agents;
